import { Component, OnInit, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SelectItem } from 'primeng/api';
import { States,
         WellTypes} from '../../../../models/models';
import { UserNameViewModel, UserClient, VendorJob, WellInfo } from '../../../../services/nswag/nswagclient';
import { IDistrictService,
         ICountyDataService,
         IOpsViewerMessagesService } from '../../../../services/services';

@Component({
  selector: 'app-job-details',
  standalone: false,
  templateUrl: './job-details.component.html',
  styleUrls: ['./job-details.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => JobDetailsComponent),
    multi: true
  }]
})
export class JobDetailsComponent implements OnInit, ControlValueAccessor {

  private _onTouched: () => void = () => {};

  @Input() _job: VendorJob = new VendorJob();
  get job() {
    return this._job;
  }
  set job(val: VendorJob) {
    this._job = val ?? new VendorJob();
    this.ensureJobDefaults(this._job);
    this.propagateChange(this._job);
  }
  @Input() modelInvalid = false;
  @Input() disabled = false;

  private _companyId: string | null = null;

  loading = true;

  supervisorOptions: SelectItem[] = [];
  companyUsers: SelectItem[] = [];
  procedures: SelectItem[] = [];
  districts: SelectItem[] = [];
  states: SelectItem[] = [];
  counties: SelectItem[] = [];
  wellTypes: SelectItem[] = [];
  millTriCones: SelectItem[] = [];

  staticDataReady = false;
  districtsReady = false;
  supervisorOptionsReady = false;
  companyUsersReady = false;

  constructor(
    private _userClient: UserClient,
    private _districtService: IDistrictService,
    private _countyDataService: ICountyDataService,
    private _messageService: IOpsViewerMessagesService
  ) { }

  ngOnInit() {
    this.ensureJobDefaults(this._job);
    this.setupDropDownOptions();
  }

  /****** ControlValueAccessor methods to handle ngModel read and updates ******/
  propagateChange = (_: any) => {};

  writeValue(obj: any): void {
    if (obj) {
      this._job = obj;
      this.ensureJobDefaults(this._job);
      this.checkIfCompanyIdChanged(this._job);
      this.checkIfEverythingIsReady();
    } else {
      this._companyId = null;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  checkIfCompanyIdChanged(job: VendorJob) {
    if (job && job.companyId) {
      if (job.companyId !== this._companyId) {
        this._companyId = job.companyId;
        this.getSupervisorOptions();
        this.getCompanyUsers();
      }
    } else {
      this.supervisorOptions = [];
      this.companyUsers = [];
    }
  }

  /****** Componet UI control methods ******/
  setupDropDownOptions() {
    this.procedures = [
      { label: 'Drill Out', value: 'Drill Out'},
      { label: 'Drill Out <2 Plugs', value: 'Drill Out <2 Plugs'},
      { label: 'DO w/ Logging Run', value: 'DO w/ Logging Run'},
      { label: 'DO w/ Fishing Trip', value: 'DO w/ Fishing Trip'},
      { label: 'Fishing Trip', value: 'Fishing Trip'},
      { label: 'Wash Out', value: 'Wash Out'},
      { label: 'Toe Prep', value: 'Toe Prep'},
      { label: 'Other', value: 'Other'}
    ];
    this.states = States;
    this.wellTypes = [
      { label: WellTypes.horizontal, value: WellTypes.horizontal },
      { label: WellTypes.vertical, value: WellTypes.vertical },
    ];
    this.millTriCones = [
      { label: 'Mill', value: 'Mill' },
      { label: 'Tri-Cone', value: 'Tri-Cone' },
      { label: 'PDC', value: 'PDC' },
    ];
    this.staticDataReady = true;

    this.getDistricts();
  }

  getSupervisorOptions() {
    this.supervisorOptions = [];
    if (this.job && this.job.companyId) {
      this._userClient.getCompanyUserNames(this.job.companyId).subscribe(
        results => {
          this.supervisorOptions = (results ?? []).map(u => ({ label: u.userName, value: u.userId }));

          const missingUserIds = [this.job.frcsDayEngineer, this.job.frcsNightEngineer]
            .filter((value): value is string => !!value)
            .filter(userId => !this.supervisorOptions.some(option => option.value === userId));

          if (!missingUserIds.length) {
            this.supervisorOptionsReady = true;
            this.checkIfEverythingIsReady();
            return;
          }

          this._userClient.getUserNames2(missingUserIds.join(',')).subscribe(
            additionalUsers => {
              this.appendMissingUserNames(this.supervisorOptions, additionalUsers);
              this.supervisorOptionsReady = true;
              this.checkIfEverythingIsReady();
            },
            error => {
              this._messageService.errorMessage(error, 'Could not get supervisor data');
              this.supervisorOptionsReady = true;
              this.checkIfEverythingIsReady();
            }
          );
        },
        error => {
          this._messageService.errorMessage(error, 'Could not get supervisor data');
          this.supervisorOptionsReady = true;
          this.checkIfEverythingIsReady();
        }
      );
    } else {
      this._messageService.warnMessage('Could not get supervisor data', 'Company not defined on job');
    }
  }

  getCompanyUsers() {
    this.companyUsers = [];
    if (this.job && this.job.companyId) {
      this._userClient.getCompanyUserNames(this.job.companyId).subscribe(
        results => {
          results.forEach(u => {
            this.companyUsers.push({ label: u.userName, value: u.userId });
          });
          this.companyUsersReady = true;
          this.checkIfEverythingIsReady();
        },
        error => { this._messageService.errorMessage(error, 'Could not get company user data'); }
      );
    } else { this._messageService.warnMessage('Could not get company user data', 'Company not defined on job'); }
  }

  getDistricts() {
    this._districtService.getDistricts().subscribe(
      results => {
        this.districts = results.map(p => Object.assign( <SelectItem>{}, p, { label: p.name, value: p.id }));
        this.districtsReady = true;
        this.checkIfEverythingIsReady();
      },
      error => { this._messageService.errorMessage(error, 'Could not get district data'); }
    );
  }

  getCounties(stateName?: string) {
   if (!stateName) {
      this.counties = [];
      return;
   }
   this.counties = [];
    this._countyDataService.getCounties(stateName).subscribe(
      results => {
        if (results !== null) {
          results.map(d => {
            this.counties.push({ label: d.name, value: d.name });
          });
      }
    },
      error => { this._messageService.errorMessage(error, 'Could not get county data'); }
    );
  }

  checkIfEverythingIsReady() {
    this.loading = !(this.staticDataReady && this.districtsReady && this.supervisorOptionsReady && this.companyUsersReady && this.job);
  }

  private ensureJobDefaults(job: VendorJob): void {
    if (!job.wellInfo) {
      job.wellInfo = new WellInfo();
    }
  }

  private appendMissingUserNames(target: SelectItem[], users: UserNameViewModel[]) {
    (users ?? []).forEach(user => {
      if (user.userId && !target.some(option => option.value === user.userId)) {
        target.push({ label: user.userName, value: user.userId });
      }
    });
  }

}
